/**
 * usePermissions Hook
 * Provides access to the granular permissions system
 *
 * Usage:
 *   const { hasPermission, permissions, isLoading } = usePermissions();
 *
 *   // Check single permission
 *   if (hasPermission('deposits.delete')) { ... }
 *
 *   // Check in render
 *   {hasPermission('users.edit_permissions') && <AdminButton />}
 */

import { useQuery } from '@tanstack/react-query';
import api from '@/services/api';

interface ResolvedPermission {
  id: string;
  category: string;
  name: string;
  description: string;
  granted: boolean;
  source: 'role_default' | 'user_override';
  roleDefault: boolean;
  override?: {
    grantedBy: string;
    grantedByName: string;
    grantedByEmail: string;
    grantedAt: string;
    reason: string | null;
  };
}

interface UserPermissionsResponse {
  userId: string;
  role: string;
  permissions: ResolvedPermission[];
}

// Fetch current user's permissions
const fetchMyPermissions = async (): Promise<UserPermissionsResponse> => {
  const response = await api.get<UserPermissionsResponse>('/permissions/me');
  // The API returns the permissions response directly (not wrapped in ApiResponse)
  return response.data as unknown as UserPermissionsResponse;
};

// Fetch specific user's permissions (admin only)
const fetchUserPermissions = async (userId: string): Promise<UserPermissionsResponse> => {
  const response = await api.get<UserPermissionsResponse>(`/permissions/users/${userId}`);
  // The API returns the permissions response directly (not wrapped in ApiResponse)
  return response.data as unknown as UserPermissionsResponse;
};

/**
 * Hook for accessing current user's permissions
 */
export const usePermissions = () => {
  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery<UserPermissionsResponse>({
    queryKey: ['permissions', 'me'],
    queryFn: fetchMyPermissions,
    staleTime: 60 * 1000, // 1 minute (matches backend cache TTL)
    retry: 2,
  });

  // Create a map for O(1) permission lookups
  const permissionMap = new Map<string, boolean>();
  if (data?.permissions) {
    for (const perm of data.permissions) {
      permissionMap.set(perm.id, perm.granted);
    }
  }

  /**
   * Check if user has a specific permission
   * @param permissionId - The permission ID (e.g., 'deposits.delete')
   * @returns boolean - Whether the user has the permission
   */
  const hasPermission = (permissionId: string): boolean => {
    return permissionMap.get(permissionId) ?? false;
  };

  /**
   * Check if user has ALL of the specified permissions
   * @param permissionIds - Array of permission IDs
   * @returns boolean - Whether the user has ALL permissions
   */
  const hasAllPermissions = (permissionIds: string[]): boolean => {
    return permissionIds.every(pid => hasPermission(pid));
  };

  /**
   * Check if user has ANY of the specified permissions
   * @param permissionIds - Array of permission IDs
   * @returns boolean - Whether the user has at least one permission
   */
  const hasAnyPermission = (permissionIds: string[]): boolean => {
    return permissionIds.some(pid => hasPermission(pid));
  };

  return {
    // User info
    userId: data?.userId,
    role: data?.role,

    // Permissions data
    permissions: data?.permissions ?? [],
    permissionMap,

    // Permission check functions
    hasPermission,
    hasAllPermissions,
    hasAnyPermission,

    // Query state
    isLoading,
    error,
    refetch,
  };
};

/**
 * Hook for accessing another user's permissions (admin only)
 * Used in the User Management modal's Permissions tab
 */
export const useUserPermissions = (userId: string | null) => {
  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery<UserPermissionsResponse>({
    queryKey: ['permissions', 'user', userId],
    queryFn: () => fetchUserPermissions(userId!),
    enabled: !!userId,
    staleTime: 30 * 1000, // 30 seconds for admin views
    retry: 2,
  });

  return {
    userId: data?.userId,
    role: data?.role,
    permissions: data?.permissions ?? [],
    isLoading,
    error,
    refetch,
  };
};

interface SetOverridesResult {
  success: boolean;
  added: string[];
  updated: string[];
  removed: string[];
  errors: string[];
}

interface ClearOverridesResult {
  success: boolean;
  removed: number;
  message: string;
}

/**
 * Set permission overrides for a user (admin only)
 */
export const setUserPermissionOverrides = async (
  userId: string,
  overrides: Array<{ permissionId: string; granted: boolean; reason?: string }>
): Promise<SetOverridesResult> => {
  const response = await api.put<SetOverridesResult>(`/permissions/users/${userId}`, { overrides });
  return response.data as unknown as SetOverridesResult;
};

/**
 * Clear all permission overrides for a user (admin only)
 */
export const clearUserPermissionOverrides = async (
  userId: string
): Promise<ClearOverridesResult> => {
  const response = await api.delete<ClearOverridesResult>(`/permissions/users/${userId}`);
  return response.data as unknown as ClearOverridesResult;
};

export default usePermissions;
